import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Clock, Plus, Trash2, X, Check, Coffee, FileText, AlertCircle
} from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import AddShiftModal from "./AddShiftModal";

// ─── Helpers ───────────────────────────────────────────────────
const toDateTimeLocal = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const offset = d.getTimezoneOffset();
    const adjusted = new Date(d.getTime() - offset * 60000);
    return adjusted.toISOString().slice(0, 16);
  } catch { return ""; }
};
const fromDateTimeLocal = (s) => {
  if (!s) return null;
  try { return new Date(s).toISOString(); }
  catch { return null; }
};
const fmtTime = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
};
const fmtRange = (start, end) => {
  if (!start) return "";
  const s = new Date(start);
  const e = end ? new Date(end) : null;
  const sStr = s.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  if (!e) return sStr;
  const sameDay = s.toDateString() === e.toDateString();
  const eStr = e.toLocaleString("en-US", sameDay ? { hour: "numeric", minute: "2-digit" } : { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  return `${sStr} – ${eStr}`;
};


// ─── Editable Breaks panel ────────────────────────────────────
// Replaces the old cramped inline add-form + tiny chips. Each break
// gets a full-width row with proper-sized inputs so you can actually
// see what you're editing.
function BreaksEditor({ entry, onClose, onSaved }) {
  // Internal shape: { id (stable for React keys), break_id?, label, start, end }
  const seed = (entry.breaks || []).map(b => ({
    id: b.break_id || Math.random().toString(36).slice(2, 10),
    break_id: b.break_id,
    label: b.label || "Break",
    start: toDateTimeLocal(b.start),
    end: toDateTimeLocal(b.end),
  }));
  const [rows, setRows] = useState(seed);
  const [saving, setSaving] = useState(false);

  // Default a new break to either 30 minutes after the shift start, or
  // right after the last break ended — whichever sits inside the shift.
  const addRow = () => {
    const last = rows[rows.length - 1];
    let startDate;
    if (last?.end) {
      startDate = new Date(last.end);
    } else {
      startDate = new Date(entry.clock_in);
      startDate.setMinutes(startDate.getMinutes() + 30);
    }
    const endDate = new Date(startDate.getTime() + 30 * 60 * 1000); // 30 min
    setRows(r => [...r, {
      id: Math.random().toString(36).slice(2, 10),
      label: "Lunch",
      start: toDateTimeLocal(startDate.toISOString()),
      end: toDateTimeLocal(endDate.toISOString()),
    }]);
  };

  const updateRow = (id, field, val) => setRows(r =>
    r.map(row => row.id === id ? { ...row, [field]: val } : row)
  );

  const removeRow = (id) => setRows(r => r.filter(row => row.id !== id));

  // Live computed duration string per row — gives instant feedback
  // when adjusting times.
  const rowDuration = (row) => {
    if (!row.start || !row.end) return "";
    const s = new Date(row.start);
    const e = new Date(row.end);
    const mins = Math.round((e - s) / 60000);
    if (Number.isNaN(mins) || mins <= 0) return "—";
    if (mins < 60) return `${mins}m`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m === 0 ? `${h}h` : `${h}h ${m}m`;
  };

  const totalMins = rows.reduce((sum, r) => {
    if (!r.start || !r.end) return sum;
    const m = Math.round((new Date(r.end) - new Date(r.start)) / 60000);
    return sum + (m > 0 ? m : 0);
  }, 0);

  const save = async () => {
    for (const r of rows) {
      if (!r.start || !r.end) { toast.error("Each break needs a start and end"); return; }
      if (new Date(r.end) <= new Date(r.start)) { toast.error("Break end must be after start"); return; }
    }
    setSaving(true);
    try {
      const payload = rows.map(r => ({
        break_id: r.break_id,
        label: (r.label || "").trim() || "Break",
        start: fromDateTimeLocal(r.start),
        end: fromDateTimeLocal(r.end),
      }));
      await api.post(`/timeclock/entries/${entry.entry_id}/breaks`, { breaks: payload });
      toast.success(rows.length === 0 ? "Breaks cleared" : `${rows.length} break${rows.length === 1 ? "" : "s"} saved`);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save breaks");
    } finally { setSaving(false); }
  };

  if (!entry.clock_out) {
    return (
      <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-2"
        data-testid={`breaks-editor-blocked-${entry.entry_id}`}>
        <AlertCircle className="w-4 h-4 text-amber-700 flex-shrink-0" />
        <p className="text-xs text-amber-800">Clock the entry out before adding or editing breaks.</p>
        <button onClick={onClose} className="ml-auto text-xs text-amber-700 hover:underline">Close</button>
      </div>
    );
  }

  return (
    <div className="mt-3 bg-amber-50/60 border border-amber-200 rounded-xl p-3.5 space-y-2"
      data-testid={`breaks-editor-${entry.entry_id}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Coffee className="w-3.5 h-3.5 text-amber-700" />
          <p className="text-xs font-bold uppercase tracking-wider text-amber-900">Breaks</p>
          <span className="text-[10px] text-slate-500">
            {rows.length} {rows.length === 1 ? "break" : "breaks"}
            {totalMins > 0 ? ` · ${totalMins < 60 ? `${totalMins}m` : `${(totalMins / 60).toFixed(2)}h`} unpaid` : ""}
          </span>
        </div>
        <button onClick={addRow} data-testid={`add-break-row-${entry.entry_id}`}
          className="text-[11px] inline-flex items-center gap-1 px-2.5 h-7 rounded-full bg-slate-900 text-white hover:bg-slate-800">
          <Plus className="w-3 h-3" /> Add break
        </button>
      </div>

      {rows.length === 0 ? (
        <p className="text-xs text-slate-500 italic py-2">No breaks logged on this shift. Tap <span className="font-medium">Add break</span> to log one.</p>
      ) : (
        <div className="space-y-2">
          {rows.map(row => (
            <div key={row.id}
              data-testid={`break-row-${row.id}`}
              className="bg-white rounded-lg border border-amber-100 p-2.5">
              {/* Stack vertically by default; switch to 2-up at md+. The
                  datetime-local inputs need ~180px of horizontal space to
                  display both the date AND the time without truncating —
                  smaller breakpoints split them onto two rows so they're
                  always fully visible. */}
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Input value={row.label} onChange={e => updateRow(row.id, "label", e.target.value)}
                    placeholder="Lunch, coffee…"
                    className="h-9 rounded-lg border-slate-200 text-sm bg-white max-w-[180px]" />
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-slate-700 tabular-nums">{rowDuration(row)}</span>
                    <button onClick={() => removeRow(row.id)}
                      data-testid={`remove-break-row-${row.id}`}
                      title="Remove this break"
                      className="w-7 h-7 rounded-full hover:bg-rose-50 flex items-center justify-center">
                      <Trash2 className="w-3.5 h-3.5 text-rose-500" />
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Start</label>
                    <Input type="datetime-local" value={row.start}
                      onChange={e => updateRow(row.id, "start", e.target.value)}
                      data-testid={`break-start-${row.id}`}
                      className="h-9 mt-0.5 rounded-lg border-slate-200 text-sm bg-white w-full" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">End</label>
                    <Input type="datetime-local" value={row.end}
                      onChange={e => updateRow(row.id, "end", e.target.value)}
                      data-testid={`break-end-${row.id}`}
                      className="h-9 mt-0.5 rounded-lg border-slate-200 text-sm bg-white w-full" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-end gap-2 pt-1.5 border-t border-amber-100">
        <button onClick={onClose} className="text-xs text-slate-500 hover:underline px-2">Cancel</button>
        <button onClick={save} disabled={saving}
          data-testid={`save-breaks-${entry.entry_id}`}
          className="text-xs font-semibold inline-flex items-center gap-1 px-4 h-8 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
          <Check className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save breaks"}
        </button>
      </div>
    </div>
  );
}

// ─── Logs panel ────────────────────────────────────────────────
export default function StaffTimeClockLogs({ staffId, staffName, defaultLocation, startDate, endDate, onChanged }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [addBreakFor, setAddBreakFor] = useState(null);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ user_id: staffId, limit: "100" });
      if (startDate) params.append("start", `${startDate}T00:00:00`);
      if (endDate)   params.append("end",   `${endDate}T23:59:59`);
      const res = await api.get(`/timeclock/entries?${params.toString()}`);
      setEntries(res.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load entries");
    }
    setLoading(false);
  }, [staffId, startDate, endDate]);
  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const removeBreak = async (entryId, breakId) => {
    if (!window.confirm("Remove this break?")) return;
    try {
      await api.delete(`/timeclock/entries/${entryId}/breaks/${breakId}`);
      toast.success("Break removed");
      fetchEntries(); onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };
  const deleteEntry = async (entryId) => {
    if (!window.confirm("Delete this shift entry? This can't be undone.")) return;
    try {
      await api.delete(`/timeclock/entries/${entryId}`);
      toast.success("Shift deleted");
      fetchEntries(); onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="mt-3 border-t border-slate-100 pt-4" data-testid={`logs-panel-${staffId}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-slate-500" />
          <p className="text-xs font-bold uppercase tracking-wider text-slate-700">Shift logs</p>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#F3F3F3] text-slate-500">{entries.length}</span>
        </div>
        <button onClick={() => setShowAdd(true)} data-testid={`add-shift-${staffId}`}
          className="text-xs inline-flex items-center gap-1 px-3 h-8 rounded-full bg-slate-900 text-white hover:bg-slate-800">
          <Plus className="w-3 h-3" /> Add shift
        </button>
      </div>

      {loading ? (
        <div className="text-xs text-slate-400 italic py-4">Loading…</div>
      ) : entries.length === 0 ? (
        <div className="text-center py-6 bg-[#FAFAFA] rounded-xl">
          <Clock className="w-6 h-6 text-slate-300 mx-auto mb-2" />
          <p className="text-xs text-slate-400">No shifts in this period</p>
          <button onClick={() => setShowAdd(true)} className="text-xs text-rose-600 hover:underline mt-1 font-medium">Add a manual shift</button>
        </div>
      ) : (
        <div className="space-y-2">
          {entries.map(e => (
            <div key={e.entry_id} className="bg-white border border-slate-200 rounded-xl p-3" data-testid={`entry-${e.entry_id}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-slate-900 tabular-nums">{fmtRange(e.clock_in, e.clock_out)}</p>
                  <div className="flex items-center flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-500 mt-0.5">
                    <span className="tabular-nums font-medium">{(e.total_hours ?? 0).toFixed(2)}h paid</span>
                    {(e.breaks || []).length > 0 && (
                      <span className="tabular-nums text-amber-700 font-medium">
                        {e.breaks.reduce((s, b) => s + (b.duration_hours || 0), 0).toFixed(2)}h breaks
                      </span>
                    )}
                    {e.source === "manual" && <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 text-[10px] font-semibold">Manual</span>}
                    {!e.clock_out && <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[10px] font-semibold">Open</span>}
                    {e.notes && <span className="italic text-slate-400 truncate max-w-[200px]" title={e.notes}>{e.notes}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => setAddBreakFor(addBreakFor === e.entry_id ? null : e.entry_id)}
                    data-testid={`toggle-breaks-${e.entry_id}`}
                    disabled={!e.clock_out}
                    title={e.clock_out ? "Manage breaks" : "Clock the entry out first"}
                    className={`text-[11px] inline-flex items-center gap-1 px-2.5 h-7 rounded-full border transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
                      addBreakFor === e.entry_id
                        ? "border-amber-300 bg-amber-100 text-amber-800"
                        : "border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}>
                    <Coffee className="w-3 h-3" /> {(e.breaks || []).length > 0 ? `Breaks · ${(e.breaks || []).length}` : "Add break"}
                  </button>
                  <button onClick={() => deleteEntry(e.entry_id)} data-testid={`delete-entry-${e.entry_id}`}
                    title="Delete entry"
                    className="w-7 h-7 rounded-full hover:bg-rose-50 flex items-center justify-center">
                    <Trash2 className="w-3.5 h-3.5 text-rose-500" />
                  </button>
                </div>
              </div>

              {/* Read-only summary chips when breaks panel is collapsed */}
              {(e.breaks || []).length > 0 && addBreakFor !== e.entry_id && (
                <div className="mt-2 flex flex-wrap gap-1" data-testid={`break-chips-${e.entry_id}`}>
                  {e.breaks.map(b => (
                    <span key={b.break_id} className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 font-medium">
                      <Coffee className="w-2.5 h-2.5" />
                      {b.label || "Break"} · {fmtTime(b.start)}–{b.end ? fmtTime(b.end) : "now"} · {(b.duration_hours || 0).toFixed(2)}h
                    </span>
                  ))}
                </div>
              )}

              {/* Spacious editor — replaces both the chips and the old cramped form */}
              {addBreakFor === e.entry_id && (
                <BreaksEditor entry={e} onClose={() => setAddBreakFor(null)}
                  onSaved={() => { setAddBreakFor(null); fetchEntries(); onChanged?.(); }} />
              )}
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <AddShiftModal staffId={staffId} staffName={staffName} defaultLocation={defaultLocation}
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); fetchEntries(); onChanged?.(); }} />
      )}
    </div>
  );
}
